import React, { useEffect, useState } from 'react';
import { Breadcrumb, Col, Form, Radio, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { MenuModifierGroupValidator } from '@ppvrbh/merchant-core';
import { RBHBox, RBHButton, RBHRadio, RBHText, RBHTextInput } from '../../../../shared/components';
import styleText from '../../../../shared/components/rbh-text/styles';
import { RBHList } from '../../../../shared/components/rbh-list';
import { Colors } from '../../../../shared/theme';
import { textStyles } from '../../../../shared/theme/fonts-ppv';
import { modifierGroupCreators } from '../redux/modifier-group-action';
import { ModifierGroup, ModifierGroupType } from '../redux/modifier-group-type';
import { ModifierItemService } from '../../ModifierItem/redux/modifier-item-service';
import { ModifierItem } from '../../ModifierItem/redux/modifier-item-type';
import { styles } from './styles';
import './styles.css';
import SelectModifierItemModal from './components/SelectModifierItemModal';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../../redux/general/general-reducer';
import { reportCreators } from '../../Report/redux/report-action';

const ModifierGroupFormScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG)) as Array<{ group: string, key: string, value: string }>;
    const maxItemPerGroup = Number(appConfig?.find(item => item.key === 'modifier.item.maximum.per.group')?.value) || 0;
    const location = useLocation<{ group?: ModifierGroup }>();

    const [form] = Form.useForm();

    const group = location.state?.group;
    const isEditing = !!group;

    const [allItems, setAllItems] = useState<ModifierItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<ModifierItem[]>(
        group?.menuModifierItems ?? []
    );
    const [groupType, setGroupType] = useState<number>(group?.menuModifierGroupType ?? ModifierGroupType.OPTIONAL);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [tempSelectedIds, setTempSelectedIds] = useState<number[]>([]);
    const [itemsError, setItemsError] = useState('');

    useEffect(() => {
        const layoutContent = document.querySelector<HTMLElement>('.ant-layout-content');
        if (layoutContent) {
            layoutContent.style.backgroundColor =  Colors.white;
        }
        
        return () => {
            if (layoutContent) {
                layoutContent.style.backgroundColor = Colors.grey50;
            }
        };
    }, []);

     useEffect(() => {
        if (!appConfig) {
            dispatch(reportCreators.callConfigAndShopList(() => {}, () => {}));
        }
    }, []);
    
    useEffect(() => {
        dispatch(httpCreators.request({
            id: 'GET_ALL_MODIFIER_ITEMS_FOR_FORM',
            sagaHandler: function* (resp) {
                if (resp.success) {
                    setAllItems(resp.data.menuModifierItems ?? []);
                }
            },
            service: ModifierItemService.getModifierItems,
            serviceParams: { limit: 1000 },
        }));
    }, []);

    useEffect(() => {
        if (group) {
            form.setFieldsValue({
                menuModifierGroupName: group.menuModifierGroupName,
                menuModifierGroupNameEN: group.menuModifierGroupNameEN,
                menuModifierGroupType: group.menuModifierGroupType,
                menuModifierGroupMax: group.menuModifierGroupMax,
                menuModifierGroupItems: group.menuModifierItems?.map(i => i.menuModifierItemId),
            });
            setGroupType(group.menuModifierGroupType);
            setSelectedItems(group.menuModifierItems ?? []);
        }
    }, [group]);

    const tableData = allItems.map((item) => ({ ...item, id: item.menuModifierItemId }));
    const listData = selectedItems.map((item) => ({ ...item, id: item.menuModifierItemId }));

    const showMaxField = groupType === ModifierGroupType.OPTIONAL || groupType === ModifierGroupType.MULTIPLE;

    const handleOpenItemModal = () => {
        setTempSelectedIds(selectedItems.map((i) => i.menuModifierItemId));
        setIsItemModalOpen(true);
    };

    const handleConfirmItemModal = () => {
        const existing = selectedItems.filter((i) => tempSelectedIds.includes(i.menuModifierItemId));
        const newIds = tempSelectedIds.filter((id) => !selectedItems.some((i) => i.menuModifierItemId === id));
        const newItems = allItems.filter((i) => newIds.includes(i.menuModifierItemId));
        setSelectedItems([...existing, ...newItems]);
        form.setFieldsValue({ menuModifierGroupItems: [...existing, ...newItems].map((i) => i.menuModifierItemId) });
        setItemsError('');
        setIsItemModalOpen(false);
    };

    const onSuccess = () => {
        setIsSubmitting(false);
        history.replace('/main/modifier-group');
    };

    const DUPLICATE_KEY_TO_FIELD: Record<string, { field: string; }> = {
        menuModifierGroupName_is_duplicated: { field: 'menuModifierGroupName' },
        menuModifierGroupNameEN_is_duplicated: { field: 'menuModifierGroupNameEN' },
    };

    const onError = (translationKeys: string[]) => {
        setIsSubmitting(false);
        const fields = translationKeys
            .filter((key) => DUPLICATE_KEY_TO_FIELD[key])
            .map((key) => ({
                name: DUPLICATE_KEY_TO_FIELD[key].field,
                errors: ['ชื่อกลุ่มตัวเลือกซ้ำ กรุณาตั้งชื่อใหม่'],
            }));
        if (fields.length > 0) {
            form.setFields(fields);
        }
    };

    const handleSubmit = () => {
        form.validateFields().catch(() => {}).then((values) => {
            if (!values) return;
            if (selectedItems.length === 0) {
                setItemsError('กรุณาเลือกตัวเลือกอย่างน้อย 1 รายการ');
                return;
            }
            
            setIsSubmitting(true);
            const payload = {
                menuModifierGroupName: values.menuModifierGroupName,
                menuModifierGroupNameEN: values.menuModifierGroupNameEN || undefined,
                menuModifierGroupType: values.menuModifierGroupType,
                menuModifierGroupItems: selectedItems.map((i) => i.menuModifierItemId),
                menuModifierGroupMax: showMaxField ? Number(values.menuModifierGroupMax) : undefined,
            };
            
            if (isEditing && group) {
                dispatch(modifierGroupCreators.callUpdateModifierGroup(
                    { ...payload, menuModifierGroupId: group.menuModifierGroupId },
                    onSuccess,
                    onError,
                ));
            } else {
                dispatch(modifierGroupCreators.callCreateModifierGroup(payload, onSuccess, onError));
            }
            setIsSubmitting(false);
        });
    };

    return (
        <RBHBox style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Breadcrumb */}
            <Breadcrumb style={{ ...styleText.breadCrumb, marginBottom: 16 }}>
                <Breadcrumb.Item>
                    <a
                        style={styleText.breadCrumbItemLink}
                        onClick={() => history.push('/main/modifier-group')}
                    >
                        <FormattedMessage id="main.menu.modifiergroup.title" />
                    </a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <span style={styleText.breadCrumbItem}>
                        <FormattedMessage id={isEditing ? 'main.menu.modifiergroup.form.edit.title' : 'main.menu.modifiergroup.form.add.title'} />
                    </span>
                </Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <RBHBox style={{ borderBottom: '1px solid #FBFBFD', paddingBottom: '16px', marginBottom: '24px' }}>
                <RBHText
                    style={styleText.headerText}
                    text={isEditing ? 'main.menu.modifiergroup.form.edit.title' : 'main.menu.modifiergroup.form.add.title'}
                />
                <RBHText style={styleText.subTitleText} text={'main.menu.modifiergroup.form.edit.subtitle'} />
            </RBHBox>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <Form form={form} layout="vertical" className="modifier-group-form">
                    {/* Info Card */}
                    <RBHBox 
                        style={{ 
                            display: 'flex', 
                            width: '100%', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            
                    }}>
                        <RBHBox style={{  
                            maxWidth: 600, 
                            width: '100%', 
                            display: 'flex', 
                            gap: 24, 
                            flexDirection: 'column' ,
                            borderWidth: '0px 1px',
                            borderColor: Colors.grey100,
                            borderStyle: 'solid',
                            backgroundColor: Colors.backgroundPaper,
                            
                        }}>
                            {/* Name section */}
                            <RBHBox style={styles.section}>
                                <Form.Item
                                    style={{ margin: 0 }}
                                    name="menuModifierGroupName"
                                    label={
                                        <RBHBox style={{ ...textStyles.subtitle2 }}>
                                            <FormattedMessage id="main.menu.modifiergroup.form.field.name" />
                                        </RBHBox>
                                    }
                                    rules={[
                                        { required: true, message: <FormattedMessage id="main.menu.modifiergroup.form.field.required" /> },
                                        { max: MenuModifierGroupValidator.menuModifierGroupName.maxLength, message: <FormattedMessage id="main.menu.modifiergroup.form.field.max50" /> },
                                        { pattern: MenuModifierGroupValidator.menuModifierGroupName.regexPattern, message: <FormattedMessage id="main.menu.modifiergroup.form.field.nameInvalid" /> },
                                    ]}
                                >
                                    <RBHTextInput
                                        id="modifier-group-name"
                                        placeholder="main.menu.modifiergroup.form.field.name"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    style={{ margin: 0 }}
                                    name="menuModifierGroupNameEN"
                                    label={
                                        <RBHBox style={{ ...textStyles.subtitle2 }}>
                                            <FormattedMessage id="main.menu.modifiergroup.form.field.nameEN" />
                                        </RBHBox>
                                    }
                                    rules={[
                                        { max: MenuModifierGroupValidator.menuModifierGroupNameEN.maxLength, message: <FormattedMessage id="main.menu.modifiergroup.form.field.max50" /> },
                                        { pattern: MenuModifierGroupValidator.menuModifierGroupNameEN.regexPattern, message: <FormattedMessage id="main.menu.modifiergroup.form.field.nameENInvalid" /> },
                                    ]}
                                >
                                    <RBHTextInput
                                        id="modifier-group-nameEN"
                                        placeholder="main.menu.modifiergroup.form.field.nameEN"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </RBHBox>
                            
                            {/* Type section */}
                            <RBHBox style={styles.section}>
                                <Form.Item
                                    style={{ margin: 0 }}
                                    name="menuModifierGroupType"
                                    initialValue={ModifierGroupType.OPTIONAL}
                                    label={
                                        <RBHBox style={{ ...textStyles.subtitle2 }}>
                                            <FormattedMessage id="main.menu.modifiergroup.form.field.type" />
                                        </RBHBox>
                                    }
                                    rules={[{ required: true, message: <FormattedMessage id="main.menu.modifiergroup.form.field.required" /> }]}
                                >
                                    <Radio.Group
                                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                                        onChange={(e) => {
                                            setGroupType(e.target.value);
                                            if (e.target.value === ModifierGroupType.SELECT_ONE) {
                                                form.setFieldsValue({ menuModifierGroupMax: undefined });
                                            }
                                        }}
                                    >
                                        <RBHRadio value={ModifierGroupType.OPTIONAL} textOption="main.menu.modifiergroup.type.optional" />
                                        <RBHRadio value={ModifierGroupType.SELECT_ONE} textOption="main.menu.modifiergroup.type.select_one" />
                                        <RBHRadio value={ModifierGroupType.MULTIPLE} textOption="main.menu.modifiergroup.type.multiple" />
                                    </Radio.Group>
                                </Form.Item>
                                {showMaxField && (
                                    <Col>
                                        <Form.Item
                                            style={{ margin: 0 }}
                                            name="menuModifierGroupMax"
                                            rules={[
                                                { required: true, message: <FormattedMessage id="main.menu.modifiergroup.form.field.required" /> },
                                                {
                                                    validator: (_, value) => {
                                                        const num = Number(value);
                                                        if (!value && value !== 0) return Promise.resolve();
                                                        if (isNaN(num) || num < 1 || num > selectedItems.length) {
                                                            return Promise.reject(<FormattedMessage id="main.menu.modifiergroup.form.field.maxInvalid" values={{ max: selectedItems.length }} />);
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                            label={
                                                <RBHBox style={{ ...textStyles.subtitle2 }}>
                                                    <FormattedMessage id="main.menu.modifiergroup.form.field.max" />
                                                </RBHBox>
                                            }
                                        >
                                            <RBHTextInput
                                                placeholder='main.menu.modifiergroup.form.field.max'
                                                id={'maximum-modifierItem-input'}
                                                style={{ width: '240px', borderColor: Colors.grey100, borderRadius: '8px' }}
                                                value={form.getFieldValue('menuModifierGroupMax')}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
                                                    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                    const currentValue = (e.target as HTMLInputElement).value
                                                    if (/^\d$/.test(e.key) && currentValue === '0') {
                                                        e.preventDefault()
                                                    }
                                                }}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const value = e.target.value;
                                                    form.setFieldsValue({ menuModifierGroupMax: value });                                          
                                                }}
                                            /> 
                                        </Form.Item>
                                    </Col>
                                )}  
                            </RBHBox>

                            {/* Items section */}
                            <RBHBox style={styles.section}>
                                {selectedItems.length > 0 && (
                                    <RBHButton
                                        icon={<PlusOutlined translate={undefined} />}
                                        style={{
                                            padding: '6px 16px',
                                            borderRadius: 48,
                                            border: 0,
                                            boxShadow: 'none',
                                            color: Colors.violet600,
                                            ...textStyles.subtitle2,
                                            position: 'absolute',
                                            right: 0,
                                            zIndex: 10,
                                        }}
                                        onClick={handleOpenItemModal}
                                        text="main.menu.modifiergroup.form.button.addItems"
                                    />
                                )}
                                <Form.Item
                                    required
                                    name="menuModifierGroupItems"
                                    rules={[{ required: true, message: <FormattedMessage id="main.menu.modifiergroup.form.field.required" /> }]}
                                    label={
                                        <RBHBox style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                            <span style={textStyles.subtitle2}>
                                                <FormattedMessage id="main.menu.modifiergroup.form.field.items" />
                                            </span>
                                            <span
                                                style={{
                                                    ...textStyles.subtitle3,
                                                    background: Colors.grey600,
                                                    color: Colors.white,
                                                    borderRadius: 24,
                                                    padding: '2px 8px',
                                                }}
                                            >
                                                {selectedItems?.length ?? 0}/{maxItemPerGroup}
                                            </span>
                                        </RBHBox>
                                    }
                                >
                                <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                                    {selectedItems.length > 0 ? (
                                        <RBHList
                                            key={selectedItems.map((i) => i.menuModifierItemId).join(',')}
                                            dragable
                                            dataSource={listData}
                                            onSort={(sortedItems) => {
                                                setSelectedItems(
                                                    sortedItems.map((item) => {
                                                        const { id, ...rest } = item;
                                                        return rest as ModifierItem;
                                                    })
                                                );
                                            }}
                                            renderItem={(item: any) => (
                                                <Row align="middle" justify="space-between" style={{ width: '100%', paddingRight: 8 }}>
                                                    <RBHBox>
                                                        <span style={{ ...textStyles.subtitle2, color: Colors.grey800 }}>
                                                            {item.menuModifierItemName}
                                                        </span>
                                                    </RBHBox>
                                                    <RBHBox>
                                                        <span style={{ ...textStyles.subtitle2, color: Colors.grey800 }}>
                                                            ฿ {item.menuModifierItemPrice}
                                                        </span>
                                                    </RBHBox>
                                                </Row>
                                            )}
                                        />
                                    ) : (
                                        <RBHButton
                                            icon={<PlusOutlined translate={undefined} />}
                                            style={{
                                                padding: '6px 16px',
                                                borderRadius: 48,
                                                border: `1px solid ${Colors.violet600}`,
                                                boxShadow: 'none',
                                                color: Colors.violet600,
                                                height: '100%',
                                                ...textStyles.subtitle2,
                                            }}
                                            onClick={handleOpenItemModal}
                                            text="main.menu.modifiergroup.form.button.addItems"
                                        />
                                    )}                                  
                                    </RBHBox>
                                </Form.Item>
                            </RBHBox>                          
                        </RBHBox>
                    </RBHBox>
                </Form>
            </div>

             <Row justify="end" align='middle' style={{ paddingTop: 16, paddingBottom: 16, gap: 8, boxShadow: '0px -4px 24px -8px #A7A9B720', position: 'sticky', bottom: 0, backgroundColor: Colors.backgroundPaper, zIndex: 10 }}>
                <RBHButton
                    data-testid="modifier-group-form-cancel"
                    style={{
                        padding: '10px 24px',
                        border: '1px solid #CDCDDC',
                        borderRadius: 48,
                        color: Colors.grey800,
                        height: '100%',
                        ...textStyles.subtitle2,
                    }}
                    onClick={() => history.push('/main/modifier-group')}
                    text="main.button_cancel"
                />
                <RBHButton
                    data-testid="modifier-group-form-submit"
                    style={{
                        padding: '10px 24px',
                        border: `1px solid ${Colors.violet600}`,
                        borderRadius: 48,
                        color: Colors.white,
                        height: '100%',
                        backgroundColor: Colors.violet600,
                        ...textStyles.subtitle2,
                    }}
                    loading={isSubmitting}
                    onClick={handleSubmit}
                    text="main.button_confirm"
                />
            </Row>

            {/* Item Selection Modal */}
            <SelectModifierItemModal
                isItemModalOpen={isItemModalOpen}
                setIsItemModalOpen={setIsItemModalOpen}
                tableData={tableData}
                tempSelectedIds={tempSelectedIds}
                setTempSelectedIds={setTempSelectedIds}
                handleConfirmItemModal={handleConfirmItemModal}
                maxItemPerGroup={maxItemPerGroup}
            />
        </RBHBox>
    );
};

export default ModifierGroupFormScreen;
